import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Grid,
  Select,
  MenuItem,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import styled from "styled-components";
import { useShopify } from "../hooks";
import ModalCountdown from "./ModalCountdown";
import "../styles/ProductPage.css";

const ProductPage = ({ product }) => {
  const { openCart, checkoutState, optionsToVariant, addVariant } = useShopify();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [variant, setVariant] = useState("");
  const [selectedOptions, setSelectedOptions] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [thumbIndex, setThumbIndex] = useState(0);
  const thumbsPerPage = 5;
  const zoomRef = useRef(null);

  useEffect(() => {
    if (product.options.length > 0) {
      const defaultOptions = {};
      product.options.forEach((option) => {
        defaultOptions[option.name] = option.values[0];
      });
      setSelectedOptions(defaultOptions);
    }
  }, [product]);

  useEffect(() => {
    if (selectedOptions) {
      const foundVariant = optionsToVariant(product, selectedOptions);
      setVariant(foundVariant);
    }
  }, [selectedOptions]);

  const handleOptionChange = (event) => {
    const { name, value } = event.target;
    setSelectedOptions((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddToCart = () => {
    if (!variant || !variant.id) return;
    addVariant(checkoutState.id, [{ variantId: variant.id, quantity }]);
    openCart();
  };

  const visibleThumbnails = product.images.slice(thumbIndex, thumbIndex + thumbsPerPage);

  const handleMouseMove = (e) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    if (zoomRef.current) {
      zoomRef.current.style.transformOrigin = `${x}% ${y}%`;
    }
  };

  return (
    <div className="product-detail-container">
      <Grid container spacing={4}>
        <Grid item xs={12} md={7} className="product-image-column">
          <div className="image-zoom-container" onMouseMove={handleMouseMove}>
            <img
              ref={zoomRef}
              src={`${variant?.image?.transformedSrc || product.images[0].transformedSrc}?width=2048`}
              alt={product.title}
              className="product-main-image"
            />
          </div>

          <div className="product-thumbnail-row">
            {thumbIndex > 0 && (
              <button
                className="thumb-nav"
                onClick={() => setThumbIndex(Math.max(0, thumbIndex - 1))}
              >
                &lt;
              </button>
            )}
            {visibleThumbnails.map((img, idx) => (
              <img
                key={idx}
                src={`${img.transformedSrc}?width=2048`}
                alt="variant"
                className="thumbnail-image"
                onClick={() => setVariant({ ...variant, image: img })}
              />
            ))}
            {thumbIndex + thumbsPerPage < product.images.length && (
              <button
                className="thumb-nav"
                onClick={() => setThumbIndex(thumbIndex + 1)}
              >
                &gt;
              </button>
            )}
          </div>
        </Grid>

        <Grid item xs={12} md={5} className="product-info-column">
          <h1 className="product-title">{product.title}</h1>
          <p className="product-price">
            ${variant?.priceV2?.amount || product.variants[0].priceV2.amount} USD
          </p>

          {product.endDate && <ModalCountdown date={product.endDate} />}

          {product.options.length > 0 && product.options[0].name !== "Title" && (
            <div className="product-options">
              {product.options.map((option) => (
                <div key={option.name} className="product-option">
                  <label>{option.name}</label>
                  <StyledSelect
                    name={option.name}
                    value={selectedOptions?.[option.name] || ""}
                    onChange={handleOptionChange}
                    variant="outlined"
                    fullWidth
                  >
                    {option.values.map((val) => (
                      <StyledMenuItem key={val} value={val}>{val}</StyledMenuItem>
                    ))}
                  </StyledSelect>
                </div>
              ))}
            </div>
          )}

          <div className="product-quantity">
            <label>Quantity</label>
            <div className="quantity-selector">
              <button onClick={() => setQuantity((q) => Math.max(1, q - 1))}>-</button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity((q) => q + 1)}>+</button>
            </div>
          </div>

          <StyledButton
            variant="contained"
            color="primary"
            onClick={handleAddToCart}
          >
            Add to cart
          </StyledButton>

          <div
            className="product-description"
            dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default ProductPage;

const StyledButton = styled(Button)`
  border-radius: 0;
  width: 100%;
  margin-top: 25px !important;
  height: 50px;
  color: white !important;
  background-color: #000 !important;
  font-size: 16px !important;
`;

const StyledSelect = styled(Select)`
  background: #f3f3f3;
  margin-top: 5px;
  font-size: 16px !important;
`;

const StyledMenuItem = styled(MenuItem)`
  font-size: 16px !important;
`;
